import {queryOptions} from '@tanstack/react-query';
import {type} from 'arktype';

import Countries from './Countries';
type Countries = typeof Countries;

export type CountryCodeFormat = 'alpha2' | 'alpha3';

export type Alpha2 = Countries[number]['alpha2'];
export type Alpha3 = Countries[number]['alpha3'];

type MaybePromise<T> = T | Promise<T>;

/**
 * There's to reasons to keep fetching list of countries:
 * - it is still 4KB gzipped and 11KB in plain text
 * - it's more future-proof to build logic that accounts for
 *   fetching countries instead of accessing them synchronously,
 *   as it is reasonable to assume we might require to fetch countries
 *   from the API
 */
export function getCountries(): MaybePromise<Countries> {
    return Countries;
}

/**
 * Because our lists of countries can change depending on requirements,
 * this type represents a value that might be a valid country code,
 * just not in our list
 */
export type CountryCodeLike = ((string & {length: 2}) | (string & {length: 3})) & {
    [Brand in {readonly SomeCountryCode: unique symbol}['SomeCountryCode']]: 'SomeCountryCode';
};
export const CountryCodeLike = type('string == 2 | string == 3').as<CountryCodeLike>();

/**
 * Because our lists of countries can change depending on requirements,
 * this type includes a `CountryCodeLike` member that represents string
 * value that might be a valid country code, just not one from our list
 */
export type SomeCountryCode = CountryCodeLike | Alpha2 | Alpha3;

export interface Country {
    readonly name: string;
    readonly alpha2: Alpha2;
    readonly alpha3: Alpha3;
}

export function useCountriesQueryOptions() {
    return queryOptions({
        queryKey: ['countries', 'record'],
        queryFn: getCountries,
        gcTime: Infinity,
        refetchOnMount: true,
        retry: 3,
    });
}

export function useCountriesListOptions() {
    return queryOptions({
        queryKey: ['countries', 'list'],
        queryFn: getCountriesList,
        gcTime: Infinity,
        refetchOnMount: true,
        retry: 3,
    });
}

export function getCountriesList(): MaybePromise<Country[]> {
    const countries = getCountries();
    if (countries instanceof Promise) {
        return countries.then(Object.values);
    } else {
        return Object.values(countries);
    }
}

export function getCountry(byCode: SomeCountryCode): MaybePromise<Country | undefined> {
    const countries = getCountries();
    if (countries instanceof Promise) {
        return countries.then(cs => cs.find(c => c.alpha2 === byCode || c.alpha3 === byCode));
    } else {
        return countries.find(c => c.alpha2 === byCode || c.alpha3 === byCode);
    }
}

export function getCountrySync(byCode: SomeCountryCode) {
    const country = getCountry(byCode);
    return country instanceof Promise ? undefined : country;
}
